// Settings → Security → "Last audit" card. Read-only for staff; super
// admins can record a new audit run via /api/security/audit-runs (used
// after ops runs the security_audit_agent).

import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Shield, Loader2, ClipboardCheck, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const VERDICT_META = {
  pass:        { icon: ShieldCheck, label: "Pass",        cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  conditional: { icon: ShieldAlert, label: "Conditional", cls: "text-amber-700  bg-amber-50  border-amber-200"   },
  fail:        { icon: ShieldAlert, label: "Fail",        cls: "text-rose-700   bg-rose-50   border-rose-200"    },
  unknown:     { icon: Shield,      label: "Unknown",     cls: "text-slate-600  bg-slate-50  border-slate-200"   },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const SEV_STYLE = {
  P0: "bg-rose-100 text-rose-700",
  P1: "bg-amber-100 text-amber-700",
  P2: "bg-sky-100 text-sky-700",
  P3: "bg-slate-100 text-slate-600",
};

export function SecurityStatusCard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [form, setForm] = useState({ verdict: "pass", summary: "", note: "", ran_by: "manual" });
  const [saving, setSaving] = useState(false);

  const isSuper = user?.role === "super_admin";

  const reload = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/security/status");
      setStatus(data);
    } catch { setStatus(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const recordRun = async () => {
    if (!form.summary.trim()) { toast.error("Add a short summary"); return; }
    try {
      setSaving(true);
      await api.post("/security/audit-runs", form);
      toast.success("Audit run recorded");
      setRecordOpen(false);
      setForm({ verdict: "pass", summary: "", note: "", ran_by: "manual" });
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to record");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm p-6">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      </Card>
    );
  }
  if (!status) return null;

  const meta = VERDICT_META[(status.verdict || "unknown").toLowerCase()] || VERDICT_META.unknown;
  const Icon = meta.icon;
  const visiblePatches = showAll ? status.patch_log : (status.patch_log || []).slice(0, 5);

  return (
    <Card data-testid="security-status-card"
          className="border-slate-200 shadow-sm p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.cls}`}
                  data-testid="security-status-verdict">
              <Icon className="h-3.5 w-3.5" /> {meta.label}
            </span>
            <span className="text-xs text-slate-500" data-testid="security-status-ran-at">
              Last audit: <span className="font-medium text-slate-700">{fmt(status.ran_at)}</span>
            </span>
          </div>
          {status.summary && (
            <p className="mt-2 text-sm text-slate-700 leading-relaxed" data-testid="security-status-summary">
              {status.summary}
            </p>
          )}
          {status.note && (
            <p className="mt-1 text-xs text-slate-400">{status.note}</p>
          )}
          <p className="mt-1 text-[11px] text-slate-400">
            Ran by <span className="font-medium">{status.ran_by || "system"}</span>
          </p>
        </div>
        {isSuper && (
          <Button size="sm" variant="outline" onClick={() => setRecordOpen(true)}
                  data-testid="security-status-record-btn" className="gap-2 shrink-0">
            <ClipboardCheck className="h-3.5 w-3.5" /> Record run
          </Button>
        )}
      </div>

      <div className="mt-5 border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Patch log · {status.patch_count} shipped
          </div>
          {(status.patch_log?.length || 0) > 5 && (
            <button onClick={() => setShowAll((s) => !s)}
                    data-testid="security-status-show-all"
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline">
              {showAll ? <>Show recent <ChevronUp className="h-3 w-3" /></> : <>Show all <ChevronDown className="h-3 w-3" /></>}
            </button>
          )}
        </div>
        <ul className="mt-2 divide-y divide-slate-100" data-testid="security-status-patch-log">
          {visiblePatches.map((p) => (
            <li key={p.id} className="flex items-start gap-3 py-2"
                data-testid={`security-patch-${p.id}`}>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${SEV_STYLE[p.severity] || SEV_STYLE.P3}`}>
                {p.severity}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800 truncate">{p.title}</div>
                <div className="text-[10px] text-slate-400">
                  Iter {p.iter} · {p.id} · shipped {p.shipped}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Record run dialog — super admin only */}
      <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
        <DialogContent data-testid="security-record-dialog">
          <DialogHeader><DialogTitle>Record security audit run</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Verdict</Label>
              <Select value={form.verdict} onValueChange={(v) => setForm({ ...form, verdict: v })}>
                <SelectTrigger data-testid="security-record-verdict"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pass">Pass</SelectItem>
                  <SelectItem value="conditional">Conditional</SelectItem>
                  <SelectItem value="fail">Fail</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Summary</Label>
              <Textarea rows={4} data-testid="security-record-summary"
                        value={form.summary}
                        onChange={(e) => setForm({ ...form, summary: e.target.value })}
                        placeholder="e.g. Iter 74 SEC-002 verified after APP_URL / FRONTEND_BASE_URL fail-closed. All Iter 73 patches still hold. Job Board clean." />
            </div>
            <div className="space-y-1.5">
              <Label>Ran by (optional)</Label>
              <input className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm"
                     data-testid="security-record-ran-by"
                     value={form.ran_by}
                     onChange={(e) => setForm({ ...form, ran_by: e.target.value })}
                     placeholder="security_audit_agent / manual" />
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <input className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm"
                     data-testid="security-record-note"
                     value={form.note}
                     onChange={(e) => setForm({ ...form, note: e.target.value })}
                     placeholder="Follow-up items, ticket numbers, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={recordRun} disabled={saving} data-testid="security-record-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
